import _ from 'lodash';
import getSlug from 'speakingurl';

const defaultTermText = 'Monthly or Daily';
const defaultStyleText = 'Private or Shared';

export const applyFilters = (
    roomTypes,
    rentalDurationTerm,
    roomSharingStyle,
    buildings,
    guestCount,
    category,
    priceMin,
    priceMax,
    portfolioId,
    newRoom,
) => {
    const filterRoomTypesPipeFunction = _.flow(
        (filteredData) => applyRentalDurationFilterForRoomType(filteredData, rentalDurationTerm),
        (filteredData) => applyRoomSharingFilterForRoomType(filteredData, roomSharingStyle),
        (filteredData) => applyBuildingFilter(filteredData, buildings),
        (filteredData) => applyGuestCountFilterForRoomType(filteredData, guestCount),
        (filteredData) => applyCategoryFilterForRoomType(filteredData, category),
        (filteredData) => applyPriceRangeFilterForRoomType(filteredData, priceMin, priceMax),
        (filteredData) => applyPortfolioFilterForRoomType(filteredData, portfolioId),
        (filteredData) => applyNewRoomFilterForRoomType(filteredData, newRoom),
    );
    return filterRoomTypesPipeFunction(roomTypes);
};

const applyRentalDurationFilterForRoomType = (roomTypes, rentalDurationFilter) => {
    if (!rentalDurationFilter || rentalDurationFilter === defaultTermText) {
        return roomTypes;
    }

    return _.reduce(
        roomTypes,
        (filteredRoomTypes, roomType) => {
            if (_.get(roomType, 'renewalDuration') === rentalDurationFilter) {
                filteredRoomTypes.push(roomType);
            }
            return _.map(filteredRoomTypes, (filteredRoomType) => filteredRoomType);
        },
        [],
    );
};

function applyRoomSharingFilterForRoomType(roomTypes, roomSharingFilter) {
    if (!roomSharingFilter || roomSharingFilter === defaultStyleText) {
        return roomTypes;
    }

    if (roomSharingFilter === 'shared_male' || roomSharingFilter === 'shared_female') {
        return _.reduce(
            roomTypes,
            (filteredRoomTypes, roomType) => {
                // Co-living
                if (
                    _.get(roomType, 'arrangementType') === 'Shared' &&
                    _.get(roomType, 'gender') === roomSharingFilter.split('_').pop()
                ) {
                    filteredRoomTypes.push(roomType);
                }
                return filteredRoomTypes;
            },
            [],
        );
    }
    if (roomSharingFilter === 'private_bedroom') {
        return _.reduce(
            roomTypes,
            (filteredRoomTypes, roomType) => {
                // Private bedroom in shared living space
                if (
                    _.get(roomType, 'arrangementType') === 'Private' &&
                    _.get(roomType, 'sharedLivingSpace')
                ) {
                    filteredRoomTypes.push(roomType);
                }
                return filteredRoomTypes;
            },
            [],
        );
    }
    // Private Apartment
    return _.reduce(
        roomTypes,
        (filteredRoomTypes, roomType) => {
            if (
                _.get(roomType, 'arrangementType') === 'Private' &&
                !_.get(roomType, 'sharedLivingSpace')
            ) {
                filteredRoomTypes.push(roomType);
            }
            return _.map(filteredRoomTypes, (filteredRoomType) => filteredRoomType);
        },
        [],
    );
}

const applyBuildingFilter = (roomTypes, buildings) => {
    if (!buildings) {
        return roomTypes;
    }

    return _.reduce(
        roomTypes,
        (filteredRoomTypes, roomType) => {
            if (typeof buildings === 'string') {
                if (_.get(roomType, 'buildingId') === buildings) {
                    filteredRoomTypes.push(roomType);
                }
            } else {
                _.forEach(buildings, (building) => {
                    if (_.get(roomType, 'buildingId') === building) {
                        filteredRoomTypes.push(roomType);
                    }
                });
            }
            return _.map(filteredRoomTypes, (filteredRoomType) => filteredRoomType);
        },
        [],
    );
};

const applyGuestCountFilterForRoomType = (roomTypes, guestCount) => {
    if (!guestCount || guestCount === 'Any') {
        return roomTypes;
    }

    return _.reduce(
        roomTypes,
        (filteredRoomTypes, roomType) => {
            if (_.get(roomType, 'maxGuests').toString() >= guestCount) {
                filteredRoomTypes.push(roomType);
            }
            return _.map(filteredRoomTypes, (filteredRoomType) => filteredRoomType);
        },
        [],
    );
};

const applyCategoryFilterForRoomType = (roomTypes, categories) => {
    if (!categories) {
        return roomTypes;
    }

    return _.reduce(
        roomTypes,
        (filteredRoomTypes, roomType) => {
            if (typeof categories === 'string') {
                if (getSlug(_.lowerCase(roomType.category)) === categories) {
                    filteredRoomTypes.push(roomType);
                }
            } else {
                _.filter(categories, (category) => {
                    if (getSlug(_.lowerCase(roomType.category)) === category) {
                        filteredRoomTypes.push(roomType);
                    }
                });
            }
            return _.map(filteredRoomTypes, (filteredRoomType) => filteredRoomType);
        },
        [],
    );
};

const applyPriceRangeFilterForRoomType = (roomTypes, priceMin, priceMax) => {
    if (!(priceMin || priceMax)) {
        return roomTypes;
    }

    return _.reduce(
        roomTypes,
        (filteredRoomTypes, roomType) => {
            const price = _.get(roomType, 'startingPrice');
            if (price >= priceMin && price <= priceMax) {
                filteredRoomTypes.push(roomType);
            }
            return filteredRoomTypes;
        },
        [],
    );
};

const applyPortfolioFilterForRoomType = (roomTypes, portfolioId) => {
    if (!portfolioId) {
        return roomTypes;
    }

    return _.reduce(
        roomTypes,
        (filteredRoomTypes, roomType) => {
            if (roomType.portfolioId === portfolioId) {
                filteredRoomTypes.push(roomType);
            }
            return _.map(filteredRoomTypes, (filteredRoomType) => filteredRoomType);
        },
        [],
    );
};

const applyNewRoomFilterForRoomType = (roomTypes, newRoom) => {
    if (!newRoom || newRoom === 'false') {
        return roomTypes;
    }

    return _.reduce(
        roomTypes,
        (filteredRoomTypes, roomType) => {
            if (roomType.acceptingProvisionalBookings) {
                filteredRoomTypes.push(roomType);
            }
            return _.map(filteredRoomTypes, (filteredRoomType) => filteredRoomType);
        },
        [],
    );
};
